import { INotificationIconConfig, INotificationItem } from '../models/notification.model';

export enum NotificationTypeEnum {
    All = '',
    Personal = 'personal',
    Social = 'social',
    Others = 'others',
    NewComment = 'new_comment',
    CommentLiked = 'comment_liked',
    PostLiked = 'post_liked',
    PostAnswered = 'post_answered',
    NewCatPost = 'new_cat_post',
    NewGroupPost = 'new_group_post',
    GroupPostReported = 'group_post_reported',
    AddedToGroup = 'added_to_group',
    GroupRequestApproved = 'group_request_approved',
    AssignedModerator = 'assigned_moderator',
    NotifyJoinedGroup = 'notify_joined_group',
    NotifyJoinedGroupRequest = 'notify_joined_group_request',
    NotifyJoinGroup = 'notify_join_group',
    NotifyJoinGroupRequest = 'notify_join_group_request',
    NotifyInviteGroupRequest = 'notify_invite_group_request',
    NewFollowRequest = 'new_follow_request',
    FollowRequestAccepted = 'follow_request_accepted',
    FollowRequestRejected = 'follow_request_rejected',
}

export const NOTIFICATION_TYPE_LABEL: Record<NotificationTypeEnum, string> = {
    [NotificationTypeEnum.All]: 'All',
    [NotificationTypeEnum.Personal]: 'Personal',
    [NotificationTypeEnum.Social]: 'Social',
    [NotificationTypeEnum.Others]: 'Others',
    [NotificationTypeEnum.NewComment]: 'New Comment',
    [NotificationTypeEnum.CommentLiked]: 'Comment Liked',
    [NotificationTypeEnum.PostLiked]: 'Post Liked',
    [NotificationTypeEnum.PostAnswered]: 'Post Answered',
    [NotificationTypeEnum.NewCatPost]: 'New Cat Post',
    [NotificationTypeEnum.NewGroupPost]: 'New Group Post',
    [NotificationTypeEnum.GroupPostReported]: 'Group Post Reported',
    [NotificationTypeEnum.AddedToGroup]: 'Added to Group',
    [NotificationTypeEnum.GroupRequestApproved]: 'Group Request Approved',
    [NotificationTypeEnum.AssignedModerator]: 'Assigned Moderator',
    [NotificationTypeEnum.NotifyJoinedGroup]: 'Joined Group',
    [NotificationTypeEnum.NotifyJoinedGroupRequest]: 'Joined Group Request',
    [NotificationTypeEnum.NotifyJoinGroup]: 'Join Group',
    [NotificationTypeEnum.NotifyJoinGroupRequest]: 'Join Group Request',
    [NotificationTypeEnum.NotifyInviteGroupRequest]: 'Invite Group Request',
    [NotificationTypeEnum.NewFollowRequest]: 'New Follow Request',
    [NotificationTypeEnum.FollowRequestAccepted]: 'Follow Request Accepted',
    [NotificationTypeEnum.FollowRequestRejected]: 'Follow Request Rejected',
};

export function getNotificationTypeLabel(type: NotificationTypeEnum): string {
    return NOTIFICATION_TYPE_LABEL[type] || type;
}

export function getNotificationRoute(notification: INotificationItem): string[] | null {
    switch (notification.type) {
        case NotificationTypeEnum.NewComment:
        case NotificationTypeEnum.CommentLiked:
        case NotificationTypeEnum.PostLiked:
        case NotificationTypeEnum.PostAnswered:
        case NotificationTypeEnum.NewCatPost:
        case NotificationTypeEnum.NewGroupPost:
            return ['/post', notification.post.id.toString()];
        case NotificationTypeEnum.AddedToGroup:
        case NotificationTypeEnum.GroupRequestApproved:
        case NotificationTypeEnum.AssignedModerator:
        case NotificationTypeEnum.NotifyJoinedGroup:
        case NotificationTypeEnum.NotifyJoinedGroupRequest:
        case NotificationTypeEnum.NotifyJoinGroup:
        case NotificationTypeEnum.NotifyInviteGroupRequest:
            return ['/groups', notification.owner.id.toString()];
        case NotificationTypeEnum.NotifyJoinGroupRequest:
            return ['/groups', notification.owner.id.toString()];
        case NotificationTypeEnum.NewFollowRequest:
            return ['/follow-requests'];
        case NotificationTypeEnum.FollowRequestAccepted:
        case NotificationTypeEnum.FollowRequestRejected:
            return ['/profile', notification.sender.id.toString()];
        default:
            return null;
    }
}

export const NOTIFICATION_TYPE_ICON: Record<NotificationTypeEnum, INotificationIconConfig> = {
    [NotificationTypeEnum.All]: {
        icon: '/assets/images/shared/user-round-blue-icon.svg', // user-round
        color: 'blue',
    },
    [NotificationTypeEnum.Personal]: {
        icon: '/assets/images/shared/user-round-blue-icon.svg', // user-round
        color: 'blue',
    },
    [NotificationTypeEnum.Social]: {
        icon: '/assets/images/shared/users-round-blue-icon.svg', // users-round
        color: 'blue',
    },
    [NotificationTypeEnum.Others]: {
        icon: '/assets/images/shared/bolt-blue-icon.svg', // bolt
        color: 'blue',
    },
    [NotificationTypeEnum.NewComment]: {
        icon: '/assets/images/shared/message-circle-plus-blue-icon.svg', // message-circle-plus
        color: 'blue',
    },
    [NotificationTypeEnum.CommentLiked]: {
        icon: '/assets/images/shared/like-red-outline-icon.svg', // heart
        color: 'red',
    },
    [NotificationTypeEnum.PostLiked]: {
        icon: '/assets/images/shared/like-red-outline-icon.svg', // heart
        color: 'red',
    },
    [NotificationTypeEnum.PostAnswered]: {
        icon: '/assets/images/shared/circle-check-big-green-icon.svg', // circle-check-big
        color: 'green',
    },
    [NotificationTypeEnum.NewCatPost]: {
        icon: '/assets/images/shared/message-circle-blue-icon.svg', // message-circle
        color: 'blue',
    },
    [NotificationTypeEnum.NewGroupPost]: {
        icon: '/assets/images/shared/users-round-blue-icon.svg', // users-round
        color: 'blue',
    },
    [NotificationTypeEnum.GroupPostReported]: {
        icon: '/assets/images/shared/message-circle-warning-yellow-icon.svg', // message-circle-warning
        color: 'yellow',
    },
    [NotificationTypeEnum.AddedToGroup]: {
        icon: '/assets/images/shared/user-round-plus-green-icon.svg', // user-round-plus
        color: 'green',
    },
    [NotificationTypeEnum.GroupRequestApproved]: {
        icon: '/assets/images/shared/circle-check-big-green-icon.svg', // circle-check-big
        color: 'green',
    },
    [NotificationTypeEnum.AssignedModerator]: {
        icon: '/assets/images/shared/shield-user-green-icon.svg', // shield-user
        color: 'green',
    },
    [NotificationTypeEnum.NotifyJoinedGroup]: {
        icon: '/assets/images/shared/user-round-check-green-icon.svg', // user-round-check
        color: 'green',
    },
    [NotificationTypeEnum.NotifyJoinedGroupRequest]: {
        icon: '/assets/images/shared/user-round-plus-blue-icon.svg', // user-round-plus
        color: 'blue',
    },
    [NotificationTypeEnum.NotifyJoinGroup]: {
        icon: '/assets/images/shared/user-round-plus-blue-icon.svg', // user-round-plus
        color: 'blue',
    },
    [NotificationTypeEnum.NotifyJoinGroupRequest]: {
        icon: '/assets/images/shared/users-round-blue-icon.svg', // users-round
        color: 'blue',
    },
    [NotificationTypeEnum.NotifyInviteGroupRequest]: {
        icon: '/assets/images/shared/users-round-blue-icon.svg', // users-round
        color: 'blue',
    },
    [NotificationTypeEnum.NewFollowRequest]: {
        icon: '/assets/images/shared/message-circle-warning-blue-icon.svg', // message-circle-warning
        color: 'blue',
    },
    [NotificationTypeEnum.FollowRequestAccepted]: {
        icon: '/assets/images/shared/user-round-check-green-icon.svg', // user-round-check
        color: 'green',
    },
    [NotificationTypeEnum.FollowRequestRejected]: {
        icon: '/assets/images/shared/user-round-x-red-icon.svg', // user-round-x
        color: 'red',
    },
};

export function getNotificationTypeIcon(type?: NotificationTypeEnum): INotificationIconConfig {
    return NOTIFICATION_TYPE_ICON[type ?? NotificationTypeEnum.All] ?? NOTIFICATION_TYPE_ICON[NotificationTypeEnum.All];
}
